import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useCanvasEngine } from "../hooks/useCanvasEngine";
import { useDrawing } from "../hooks/useDrawing";
import { useFog } from "../hooks/useFog";
import { useDynamicFog } from "../hooks/useDynamicFog";
import type { UseDynamicFogReturn } from "../hooks/useDynamicFog";
import { useWalls } from "../hooks/useWalls";
import { useLocationLinks } from "../hooks/useLocationLinks";
import { useMapObjects } from "../hooks/useMapObjects";
import type { UseMapObjectsReturn } from "../hooks/useMapObjects";
import { useSessionContext } from "./SessionContext";
import { useUndoContext } from "./UndoContext";
import { createDrawingEraseCommand } from "../lib/undo-commands";
import type { Wall, FogReveal, SerializedToken, GlobalIllumination, PageState } from "@roll1/shared";
type CanvasEngine = ReturnType<typeof useCanvasEngine>;
type UseDrawingReturn = ReturnType<typeof useDrawing>;
type UseFogReturn = ReturnType<typeof useFog>;
type UseWallsReturn = ReturnType<typeof useWalls>;
type UseLocationLinksReturn = ReturnType<typeof useLocationLinks>;

export interface WallChainPreview {
  points: Array<{ x: number; y: number }>;
  previewPoint: { x: number; y: number } | null;
  hoveredWallId: string | null;
  /** Snap indicator: when cursor is near an existing wall endpoint */
  snapPoint: { x: number; y: number } | null;
}

export interface CanvasContextValue {
  engine: CanvasEngine;
  drawing: UseDrawingReturn;
  fog: UseFogReturn;
  dynamicFog: UseDynamicFogReturn;
  dynamicFogTokensRef: React.MutableRefObject<SerializedToken[]>;
  wallsHook: UseWallsReturn;
  walls: React.MutableRefObject<Wall[]>;
  wallChainPreview: React.MutableRefObject<WallChainPreview>;
  locationLinks: UseLocationLinksReturn;
  mapObjects: UseMapObjectsReturn;
  globalIlluminationRef: React.MutableRefObject<GlobalIllumination>;
  controlledTokenIdsRef: React.MutableRefObject<Set<string>>;
  snapToGrid: boolean;
  setSnapToGrid: React.Dispatch<React.SetStateAction<boolean>>;
  drawColor: string;
  setDrawColor: (color: string) => void;
  drawWidth: number;
  setDrawWidth: (width: number) => void;
}

const CanvasContext = createContext<CanvasContextValue | null>(null);

export function CanvasProvider({ children }: { children: ReactNode }) {
  const { mp, isGM, sessionSettings, pages, outgoingPageId } =
    useSessionContext();
  const undoStack = useUndoContext();
  const engine = useCanvasEngine();

  // A ruler is drawn on the map of this canvas: players get it only when it is theirs.
  const rulerPageIdRef = useRef(outgoingPageId);
  rulerPageIdRef.current = outgoingPageId;

  const eraseBatchRef = useRef<string[]>([]);
  const eraseTimerRef = useRef<number>(0);

  const drawing = useDrawing(engine, {
    onRulerShow: (points, totalDistance, persistent) => {
      if (mp.session) {
        mp.send({
          type: "ruler:show",
          points,
          totalDistance,
          persistent,
          pageId: rulerPageIdRef.current,
        } as any);
      }
    },
    onRulerHide: () => {
      if (mp.session) {
        mp.send({ type: "ruler:hide", pageId: rulerPageIdRef.current } as any);
      }
    },
    onEraseStroke: (strokeIndex, stroke) => {
      undoStack.push(
        createDrawingEraseCommand({
          stroke,
          addStroke: (restoredStroke) => {
            drawing.strokes.current.push(restoredStroke);
            drawing.invalidateCache();
            engine.markDirty("drawing");
          },
          removeStrokeById: (strokeId) => {
            drawing.strokes.current = drawing.strokes.current.filter(
              (item) => item.id !== strokeId,
            );
            drawing.invalidateCache();
            engine.markDirty("drawing");
          },
          sendStroke: (restoredStroke) => {
            if (mp.session) {
              mp.send({
                type: "drawing:stroke",
                stroke: restoredStroke,
                pageId: outgoingPageId,
              } as any);
            }
          },
          sendErase: (strokeId) => {
            if (mp.session) {
              mp.send({
                type: "drawing:erase",
                strokeId,
                pageId: outgoingPageId,
              } as any);
            }
          },
        }),
      );
      if (mp.session) {
        eraseBatchRef.current.push(stroke.id);
        if (eraseTimerRef.current) clearTimeout(eraseTimerRef.current);
        eraseTimerRef.current = window.setTimeout(() => {
          const ids = eraseBatchRef.current.splice(0);
          if (ids.length > 0 && mp.session) {
            mp.send({
              type: "drawing:erase-batch",
              strokeIds: ids,
              pageId: outgoingPageId,
            } as any);
          }
        }, 80);
      }
    },
    measurementMode: sessionSettings.measurementMode,
    gridSizeFt: sessionSettings.gridSizeFt,
  });

  const fog = useFog(engine, isGM);

  const wallsHookReturn = useWalls(
    engine,
    (msg) => { if (mp.session) mp.send(msg); },
    isGM,
  );

  // Tokens ref for dynamic fog — uses SerializedToken[] from page data
  const dynamicFogTokensRef = useRef<SerializedToken[]>([]);
  const controlledTokenIdsRef = useRef(new Set<string>());

  // Lighting refs for dynamic fog
  const globalIlluminationRef = useRef<GlobalIllumination>({ level: 'bright' });
  const pageDataRef = useRef<PageState | null>(null);
  const gridSizeFtRef = useRef<number>(sessionSettings.gridSizeFt ?? 5);
  // Keep the ref in sync with the live setting — it was frozen at the
  // first-render value, so dynamic fog and vision computed all radii against
  // the default 5 ft even after the GM changed the grid scale.
  useEffect(() => {
    gridSizeFtRef.current = sessionSettings.gridSizeFt ?? 5;
  }, [sessionSettings.gridSizeFt]);

  // Map objects hook (before dynamic fog, so ref is available)
  const mapObjects = useMapObjects({ engine, isGM, gridSizeFtRef });

  // Dynamic fog hook
  const dynamicFog = useDynamicFog(
    engine,
    wallsHookReturn.walls,
    wallsHookReturn.wallsVersion,
    dynamicFogTokensRef,
    fog.revealed,
    isGM,
    controlledTokenIdsRef,
    (explored: FogReveal[]) => {
      if (mp.session) {
        mp.send({
          type: 'fog:explored-update',
          explored,
          pageId: outgoingPageId,
        } as any);
      }
    },
    globalIlluminationRef,
    pageDataRef,
    gridSizeFtRef,
    mapObjects.objects,
  );

  // Wire dynamic fog renderer into useFog
  useEffect(() => {
    fog.setDynamicFogRenderer(dynamicFog.renderDynamicFog);
    return () => fog.setDynamicFogRenderer(null);
  }, [fog, dynamicFog.renderDynamicFog]);

  // controlledTokenIdsRef is WRITTEN by useControlledTokensSync (mounted below
  // TokenProvider/SheetProvider in App.tsx) from live stores. The effect that
  // used to live here read the frozen join snapshot + page-store cache and was
  // the root of the "works only after F5" class (22.09.2026).

  // Sync tokens and explored from page data for dynamic fog
  useEffect(() => {
    const pid = pages.viewingPageId;
    if (!pid) return;
    const pd = pages.getPageData(pid);
    if (pd) {
      dynamicFogTokensRef.current = pd.tokens ?? [];
      dynamicFog.setExplored([...(pd.fog.explored ?? [])]);
      // BI-001: sync fog mode
      fog.fogModeRef.current = pd.fog.mode ?? 'manual';
      // Dynamic mode requires fog enabled
      if (fog.fogModeRef.current === 'dynamic' && !fog.enabled.current) {
        fog.enabled.current = true;
        engine.markDirty('fog');
      }
      // Sync lighting refs
      pageDataRef.current = pd;
      globalIlluminationRef.current = pd.globalIllumination ?? { level: 'bright' };
    }
  }, [pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // BI-002: invalidate dynamic fog caches on page switch
  useEffect(() => {
    dynamicFog.invalidateAll();
  }, [pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const locationLinks = useLocationLinks({
    engine,
    isGM,
    pageIds: pages.pages.map((p) => p.id),
  });

  const wallChainPreview = useRef<WallChainPreview>({ points: [], previewPoint: null, hoveredWallId: null, snapPoint: null });

  const [snapToGrid, setSnapToGrid] = useState(false);
  const [drawColor, setDrawColor] = useState("#ff0000");
  const [drawWidth, setDrawWidth] = useState(3);

  // Sync draw settings to refs
  useEffect(() => {
    drawing.color.current = drawColor;
  }, [drawColor, drawing]);
  useEffect(() => {
    drawing.lineWidth.current = drawWidth;
  }, [drawWidth, drawing]);

  // Sync gridSize/mapScale/bgColor from active page to engine refs (only on page switch)
  useEffect(() => {
    const pid = pages.viewingPageId;
    if (!pid) return;
    const pd = pages.getPageData(pid);
    if (pd) {
      engine.gridSizeRef.current = pd.gridSize ?? 70;
      engine.mapScaleRef.current = (pd as unknown as Record<string, unknown>).mapScale as number ?? 1.0;
      engine.mapOffsetRef.current = { x: pd.mapOffsetX ?? 0, y: pd.mapOffsetY ?? 0 };
      engine.bgColorRef.current = ((pd as unknown as Record<string, unknown>).bgColor as string) || null;
      engine.markAllDirty();
    }
  }, [pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  // V004: Sync gridStyle/gridOpacity from SessionSettings to engine refs
  useEffect(() => {
    if (engine.gridStyleRef) engine.gridStyleRef.current = sessionSettings.gridStyle ?? 'lines';
    if (engine.gridOpacityRef) engine.gridOpacityRef.current = sessionSettings.gridOpacity ?? 100;
    engine.markDirty('grid');
  }, [sessionSettings.gridStyle, sessionSettings.gridOpacity, engine]);

  const value = useMemo(
    () => ({
      engine,
      drawing,
      fog,
      dynamicFog,
      dynamicFogTokensRef,
      wallsHook: wallsHookReturn,
      walls: wallsHookReturn.walls,
      wallChainPreview,
      globalIlluminationRef,
      controlledTokenIdsRef,
      locationLinks,
      mapObjects,
      snapToGrid,
      setSnapToGrid,
      drawColor,
      setDrawColor,
      drawWidth,
      setDrawWidth,
    }),
    [
      engine,
      drawing,
      fog,
      dynamicFog,
      wallsHookReturn,
      locationLinks,
      mapObjects,
      snapToGrid,
      setSnapToGrid,
      drawColor,
      setDrawColor,
      drawWidth,
      setDrawWidth,
    ],
  );

  return (
    <CanvasContext.Provider value={value}>{children}</CanvasContext.Provider>
  );
}

export function useCanvasContext() {
  const ctx = useContext(CanvasContext);
  if (!ctx)
    throw new Error("useCanvasContext must be used within CanvasProvider");
  return ctx;
}
